import React, { useState, useEffect } from 'react'
import styled from 'styled-components'
import { Form, Spinner } from 'react-bootstrap'
import Modal from '../../../common/Modal'
import RowSpacer from '../../../common/styles/RowSpacer'
import ColSpacer from '../../../common/styles/ColSpacer'
import BooksitoutButton from '../../../common/button/BooksitoutButton'
import { BooksitoutServer } from '../../../config/BooksitoutServer'
import toast from 'react-hot-toast'
import useBookSubscription from '../../search/bookSource/hooks/useBookSubscription'
import useBookLibraryOnline from '../../search/bookSource/hooks/useBookLibraryOnline'
import useBookUsedOnline from '../../search/bookSource/hooks/useBookUsedOnline'
import useBookUsedOffline from '../../search/bookSource/hooks/useBookUsedOffline'
import useSearchSettings from '../../search/bookSource/hooks/useBookSearchSettings'
import BookSearchSettingsResponse, { getLibrarySearchScope } from '../../appUsers/settings/response/BookSearchSettingsResponse'
import utils from '../../../common/utils'
import BooksitoutIcon from '../../../config/BooksitoutIcon'

interface Props {
	bookId: number
	isbn: string | null
	isOpen: boolean
	onClose: () => void
	currentProvider?: string | null
	onSuccess?: (readingSource: { provider: string; label: string; imageUrl: string | null; memo: string | null }) => void
}

interface SelectedSource {
	provider: string
	label: string
	imageUrl?: string | null
	defaultMemo?: string
	defaultMemoBadges?: string[]
	libraryId?: number
	forceShowMemo?: boolean
}

const BookReadingSourceModal: React.FC<Props> = ({ bookId, isbn, isOpen, onClose, currentProvider, onSuccess }) => {
	const [settings, refreshSettings] = useSearchSettings()
	const skipMemo = !(settings?.isReadingSourceMemoEnabled ?? true)
	const [selected, setSelected] = useState<SelectedSource | null>(null)
	const [memo, setMemo] = useState('')
	const [memoBadges, setMemoBadges] = useState<string[]>([])
	const [disableMemoNext, setDisableMemoNext] = useState(false)

	useEffect(() => {
		if (!isOpen) {
			setSelected(null)
			setMemo('')
			setMemoBadges([])
			setDisableMemoNext(false)
		}
	}, [isOpen])

	const onSubmit = (e: React.FormEvent<HTMLFormElement>) => {
		e.preventDefault()
		if (!selected) return

		const combinedMemo = [...memoBadges, memo].filter(Boolean).join(', ') || null

		BooksitoutServer
			.put(`/v1/book/${bookId}`, {
				readingSourceProvider: selected.provider,
				readingSourceLibraryId: selected.libraryId ?? null,
				readingSourceMemo: combinedMemo,
			})
			.then(() => {
				toast.success('책 구할 곳을 저장했어요')
				onClose()
				onSuccess?.({
					provider: selected.provider,
					label: selected.label,
					imageUrl: selected.imageUrl ?? null,
					memo: combinedMemo,
				})
				if (disableMemoNext && settings) {
					BooksitoutServer.put('/v1/book/search/settings', {
						librarySearchMethod: settings.librarySearchMethod,
						regionIds: settings.regionDetails.map(r => r.id),
						libraryIds: settings.libraries.map(l => l.id),
						isReadingSourceMemoEnabled: false,
					}).then(() => refreshSettings())
				}
			})
			.catch(() => toast.error('오류가 났어요. 잠시 후 다시 시도해 주세요.'))
	}

	return (
		<Modal
			titleText={selected ? `메모 입력 - ${selected.label}` : '책 구할 곳 지정하기'}
			onTitleClick={selected ? undefined : () => { window.location.href = '/settings/book-shelf' }}
			isShowing={isOpen}
			onClose={() => {
				if (selected) {
					setSelected(null)
				} else {
					onClose()
				}
			}}
			size='lg'
			body={
				selected ? (
					<Form onSubmit={onSubmit}>
						<Form.Group>
							<Form.Label>메모 (선택)</Form.Label>
							{memoBadges.length > 0 && (
								<MemoBadgeList>
									{memoBadges.map((badge, i) => (
										<MemoBadge key={i} onClick={() => setMemoBadges(prev => prev.filter((_, j) => j !== i))}>
											{badge}
											<MemoBadgeRemove>
												<BooksitoutIcon.xmark />
											</MemoBadgeRemove>
										</MemoBadge>
									))}
								</MemoBadgeList>
							)}
							<Form.Control
								type="text"
								placeholder="메모"
								value={memo}
								onChange={e => setMemo(e.target.value)}
								autoComplete="off"
								autoFocus
							/>
						</Form.Group>

						{!selected.forceShowMemo && (
							<>
								<RowSpacer size={15} />
								<Form.Check
									id="disable-memo-next"
									type="checkbox"
									label="다음에는 메모 추가하지 않기"
									checked={disableMemoNext}
									onChange={() => setDisableMemoNext(prev => !prev)}
								/>
							</>
						)}

						<RowSpacer size={30} />
						<BooksitoutButton message={'책 구할 곳 지정하기'} />
					</Form>
				) : (
					<SourceList
						isbn={isbn}
						currentProvider={currentProvider}
						settings={settings}
						onSelect={(source) => {
							if (source.provider === 'BOUGHT' || source.provider === 'SHOULD_BUY_NEW' || (skipMemo && !source.forceShowMemo)) {
								BooksitoutServer
									.put(`/v1/book/${bookId}`, {
										readingSourceProvider: source.provider,
										readingSourceLibraryId: source.libraryId ?? null,
										readingSourceMemo: null,
									})
									.then(() => {
										toast.success('책 구할 곳을 저장했어요')
										onClose()
										onSuccess?.({
											provider: source.provider,
											label: source.label,
											imageUrl: source.imageUrl ?? null,
											memo: null,
										})
									})
									.catch(() => toast.error('오류가 났어요. 잠시 후 다시 시도해 주세요.'))
							} else {
								setSelected(source)
								setMemoBadges(source.defaultMemoBadges ?? [])
								setMemo(source.defaultMemo ?? '')
							}
						}}
					/>
				)
			}
		/>
	)
}

interface SourceListProps {
	isbn: string | null
	currentProvider?: string | null
	onSelect: (source: SelectedSource) => void
	settings: BookSearchSettingsResponse | null
}

const SourceList: React.FC<SourceListProps> = ({ isbn, currentProvider, onSelect, settings }) => {
	const [subscriptions, isSubscriptionLoading] = useBookSubscription(isbn ?? '')
	const [librariesOnline, isLibraryOnlineLoading] = useBookLibraryOnline(isbn ?? '')
	const [librariesOffline, setLibrariesOffline] = useState<{ provider: string; label: string; logo: string; libraryId: number }[]>([])
	const [isLibraryOfflineLoading, setIsLibraryOfflineLoading] = useState(true)
	useEffect(() => {
		if (isbn) {
			BooksitoutServer
				.get(`/v1/book/search/sources/library/offline?isbn13=${isbn}`)
				.then(res => setLibrariesOffline(res.data.map((lib: any) => ({
					provider: 'LIBRARY_OFFLINE',
					label: lib.library.name,
					logo: lib.library.logo,
					libraryId: lib.library.id,
				}))))
				.catch(() => {})
				.finally(() => setIsLibraryOfflineLoading(false))
		} else {
			setIsLibraryOfflineLoading(false)
		}
	}, [isbn])
	const [usedOnline, isUsedOnlineLoading] = useBookUsedOnline(isbn ?? '')
	const [usedOffline, isUsedOfflineLoading] = useBookUsedOffline(isbn ?? '')

	const [allProviders, setAllProviders] = useState<{ provider: string; label: string; imageUrl: string }[]>([])
	useEffect(() => {
		BooksitoutServer
			.get('/v1/book/reading-source-providers')
			.then(res => setAllProviders(res.data))
			.catch(() => {})
	}, [])

	const librarySearchScope = getLibrarySearchScope(settings)

	const getEtcProvider = (provider: string) => allProviders.find(p => p.provider === provider)
	const boughtProvider = allProviders.find(p => p.provider === 'BOUGHT')
	const shouldBuyNewProvider = allProviders.find(p => p.provider === 'SHOULD_BUY_NEW')
	const etcProvider = allProviders.find(p => p.provider === 'ETC')

	return (
		<div>
			{boughtProvider && (
				<SourceItem
					$isSelected={currentProvider === 'BOUGHT'}
					onClick={() => onSelect({ provider: 'BOUGHT', label: boughtProvider.label })}
				>
					<SourceItemContent>
						<BooksitoutIcon.bought size={18} color="var(--primary-color)" />
						<ColSpacer size={8} />
						<SourceLabel>{boughtProvider.label}</SourceLabel>
					</SourceItemContent>
				</SourceItem>
			)}
			{shouldBuyNewProvider && (
				<SourceItem
					$isSelected={currentProvider === 'SHOULD_BUY_NEW'}
					onClick={() => onSelect({ provider: 'SHOULD_BUY_NEW', label: shouldBuyNewProvider.label })}
				>
					<SourceItemContent>
						<BooksitoutIcon.shouldBuy size={18} color="var(--danger)" />
						<ColSpacer size={8} />
						<SourceLabel>{shouldBuyNewProvider.label}</SourceLabel>
					</SourceItemContent>
				</SourceItem>
			)}
			<RowSpacer />

			{!isbn && (
				<>
					<EmptyMessage>ISBN이 없어서 출처를 검색할 수 없어요</EmptyMessage>
					<RowSpacer />
				</>
			)}

			{isbn && (
				<>
					{/* 도서관 */}
					{isLibraryOfflineLoading ? (
						<>
							<SectionTitleRow>
								<div>
									<SectionTitle>도서관</SectionTitle>
									<SectionSubTitle>{librarySearchScope}</SectionSubTitle>
								</div>
								<SettingsLink href="/settings/search"><BooksitoutIcon.settings /></SettingsLink>
							</SectionTitleRow>
							<SectionLoadingContainer><Spinner animation="border" variant="book" size="sm" /></SectionLoadingContainer>
							<RowSpacer />
						</>
					) : librariesOffline.length > 0 && (
						<>
							<SectionTitleRow>
								<div>
									<SectionTitle>도서관</SectionTitle>
									<SectionSubTitle>{librarySearchScope}</SectionSubTitle>
								</div>
								<SettingsLink href="/settings/search"><BooksitoutIcon.settings /></SettingsLink>
							</SectionTitleRow>
							{librariesOffline.map((lib, i) => (
								<SourceItem
									key={`offline-lib-${i}`}
									$isSelected={currentProvider === lib.provider}
									onClick={() => onSelect({ provider: lib.provider, label: lib.label, imageUrl: lib.logo, libraryId: lib.libraryId })}
								>
									<SourceItemContent>
										{lib.logo ? <SourceIcon src={lib.logo} /> : <PlaceholderIcon />}
										<ColSpacer size={8} />
										<SourceLabel>{lib.label}</SourceLabel>
									</SourceItemContent>
								</SourceItem>
							))}
							<RowSpacer />
						</>
					)}

					{/* 인터넷 도서관 */}
					<SectionTitle>인터넷 도서관</SectionTitle>
					{isLibraryOnlineLoading ? (
						<SectionLoadingContainer><Spinner animation="border" variant="book" size="sm" /></SectionLoadingContainer>
					) : librariesOnline.length === 0 ? null : (
						librariesOnline.map(lib => (
							<SourceItem
								key={lib.provider}
								$isSelected={currentProvider === lib.provider}
								onClick={() => onSelect({ provider: lib.provider, label: lib.label, imageUrl: lib.imageUrl })}
							>
								<SourceItemContent>
									<SourceIcon src={lib.imageUrl} />
									<ColSpacer size={8} />
									<SourceLabel>{lib.label}</SourceLabel>
								</SourceItemContent>
								</SourceItem>
						))
					)}
					{!isLibraryOnlineLoading && <EtcProviderItem provider={getEtcProvider('LIBRARY_ONLINE_ETC')} currentProvider={currentProvider} onSelect={onSelect} />}

					<RowSpacer />

					{/* 구독 서비스 */}
					<SectionTitle>구독 서비스</SectionTitle>
					{isSubscriptionLoading ? (
						<SectionLoadingContainer><Spinner animation="border" variant="book" size="sm" /></SectionLoadingContainer>
					) : subscriptions.length === 0 ? null : (
						subscriptions.map(sub => (
							<SourceItem
								key={sub.provider}
								$isSelected={currentProvider === sub.provider}
								onClick={() => onSelect({ provider: sub.provider, label: sub.label, imageUrl: sub.imageUrl })}
							>
								<SourceItemContent>
									<SourceIcon src={sub.imageUrl} />
									<ColSpacer size={8} />
									<SourceLabel>{sub.label}</SourceLabel>
								</SourceItemContent>
							</SourceItem>
						))
					)}
					{!isSubscriptionLoading && <EtcProviderItem provider={getEtcProvider('SUBSCRIPTION_ETC')} currentProvider={currentProvider} onSelect={onSelect} />}

					<RowSpacer />

					{/* 중고 (온라인) */}
					<SectionTitle>중고 (온라인)</SectionTitle>
					{isUsedOnlineLoading ? (
						<SectionLoadingContainer><Spinner animation="border" variant="book" size="sm" /></SectionLoadingContainer>
					) : usedOnline.length === 0 ? null : (
						usedOnline.map(used => (
							<SourceItem
								key={used.provider}
								$isSelected={currentProvider === used.provider}
								onClick={() => onSelect({ provider: used.provider, label: used.label, imageUrl: used.imageUrl, forceShowMemo: true })}
							>
								<SourceItemContent>
									<SourceIcon src={used.imageUrl} />
									<ColSpacer size={8} />
									<SourceLabel>{used.label}</SourceLabel>
									<ColSpacer size={8} />
									<SourceDetail>최저 {utils.insertCommas(used.minPrice)}원 · {used.stockCount}권</SourceDetail>
								</SourceItemContent>
								</SourceItem>
						))
					)}
					{!isUsedOnlineLoading && <EtcProviderItem provider={getEtcProvider('USED_ONLINE_ETC')} currentProvider={currentProvider} onSelect={onSelect} />}

					<RowSpacer />

					{/* 중고 (매장) */}
					<SectionTitle>중고 (매장)</SectionTitle>
					{isUsedOfflineLoading ? (
						<SectionLoadingContainer><Spinner animation="border" variant="book" size="sm" /></SectionLoadingContainer>
					) : usedOffline.length === 0 ? null : (
						usedOffline.map(used => (
							<SourceItem
								key={used.provider}
								$isSelected={currentProvider === used.provider}
								onClick={() => onSelect({ provider: used.provider, label: used.label, imageUrl: used.imageUrl, defaultMemoBadges: used.locationList ?? [], forceShowMemo: true })}
							>
								<div>
									<SourceItemContent>
										<SourceIcon src={used.imageUrl} />
										<ColSpacer size={8} />
										<SourceLabel>{used.label}</SourceLabel>
										<ColSpacer size={8} />
										<SourceDetail>최저 {utils.insertCommas(used.minPrice)}원 · {used.stockCount}권</SourceDetail>
									</SourceItemContent>
									{used.locationList?.length > 0 && (
										<LocationBadgeList>
											{used.locationList.map((loc, i) => (
												<LocationBadge key={i}>{loc}</LocationBadge>
											))}
										</LocationBadgeList>
									)}
								</div>
								</SourceItem>
						))
					)}
					{!isUsedOfflineLoading && <EtcProviderItem provider={getEtcProvider('USED_OFFLINE_ETC')} currentProvider={currentProvider} onSelect={onSelect} />}

					<RowSpacer />
				</>
			)}

			{/* 기타 */}
			{etcProvider && (
				<>
					<SectionTitle>기타</SectionTitle>
					<SourceItem
						$isSelected={currentProvider === 'ETC'}
						onClick={() => onSelect({ provider: 'ETC', label: etcProvider.label })}
					>
						<SourceItemContent>
							<PlaceholderIcon />
							<ColSpacer size={8} />
							<SourceLabel>{etcProvider.label}</SourceLabel>
						</SourceItemContent>
					</SourceItem>
				</>
			)}
		</div>
	)
}

const SectionTitleRow = styled.div`
	display: flex;
	align-items: center;
	justify-content: space-between;
`

const SectionTitle = styled.h6`
	font-weight: 700;
	font-size: 0.95rem;
	margin-bottom: 8px;
	color: var(--text-color);
`

const SectionSubTitle = styled.span`
	font-size: 0.75rem;
	color: var(--text-secondary-color);
	padding-bottom: 8px;
	display: block;
`

const SettingsLink = styled.a`
	color: var(--text-secondary-color);
	font-size: 14px;

	&:hover {
		color: var(--text-color);
	}
`

const SourceItem = styled.div<{ $isSelected: boolean }>`
	display: flex;
	align-items: center;
	justify-content: space-between;
	padding: 10px 12px;
	margin-left: 10px;
	margin-bottom: 4px;
	border-radius: 10px;
	cursor: pointer;
	background-color: ${props => props.$isSelected ? 'var(--card-card-bg-color)' : 'transparent'};

	&:hover {
		background-color: var(--card-card-bg-color);
	}
`

const SourceItemContent = styled.div`
	display: flex;
	align-items: center;
`

const SourceIcon = styled.img`
	width: 24px;
	height: 24px;
	border-radius: 6px;
`

const PlaceholderIcon = styled.div`
	width: 24px;
	height: 24px;
	border-radius: 6px;
	background-color: #ccc;
	flex-shrink: 0;
`

const SourceLabel = styled.span`
	font-size: 0.9rem;
	font-weight: 500;
`

const SourceDetail = styled.span`
	font-size: 0.8rem;
	color: var(--text-secondary-color);
`

const SectionLoadingContainer = styled.div`
	display: flex;
	justify-content: center;
	align-items: center;
	min-height: 60px;
`

const EmptyMessage = styled.p`
	font-size: 0.85rem;
	color: var(--text-secondary-color);
	margin: 4px 0;
`

const MemoBadgeList = styled.div`
	display: flex;
	flex-wrap: wrap;
	gap: 6px;
	margin-bottom: 12px;
`

const MemoBadge = styled.span`
	display: inline-flex;
	align-items: center;
	gap: 4px;
	background-color: var(--badge-bg-color);
	color: white;
	font-size: 0.8rem;
	font-weight: 600;
	border-radius: 8px;
	padding: 4px 8px;
	cursor: pointer;

	&:hover {
		opacity: 0.8;
	}
`

const MemoBadgeRemove = styled.span`
	cursor: pointer;
	display: inline-flex;
	align-items: center;
	font-size: 10px;
	opacity: 0.7;

	&:hover {
		opacity: 1;
	}
`

const LocationBadgeList = styled.div`
	display: flex;
	flex-wrap: wrap;
	gap: 4px;
	margin-top: 6px;
	padding-left: 32px;
`

const LocationBadge = styled.span`
	background-color: var(--badge-bg-color);
	color: white;
	font-size: 0.7rem;
	font-weight: bold;
	border-radius: 5px;
	padding: 2px 6px;
`

interface EtcProviderItemProps {
	provider?: { provider: string; label: string; imageUrl: string }
	currentProvider?: string | null
	onSelect: (source: SelectedSource) => void
}

const EtcProviderItem: React.FC<EtcProviderItemProps> = ({ provider, currentProvider, onSelect }) => {
	if (!provider) return null
	return (
		<SourceItem
			$isSelected={currentProvider === provider.provider}
			onClick={() => onSelect({ provider: provider.provider, label: provider.label })}
		>
			<SourceItemContent>
				<PlaceholderIcon />
				<ColSpacer size={8} />
				<SourceLabel>{provider.label}</SourceLabel>
			</SourceItemContent>
		</SourceItem>
	)
}

export default BookReadingSourceModal
